'use client';

import banner1 from '@/assets/images/convention/contactus.jpg';
import { useStyle } from '@/classify';
import Button from '@/components/Button';
import Field from '@/components/Field';
import FormError from '@/components/FormError';
import { fullPageLoadingIndicator } from '@/components/LoadingIndicator';
import TextArea from '@/components/TextArea';
import TextInput from '@/components/TextInput';
import { getCurrentStoreContactConfig } from '@/constants/contact-config';
import { isRequired } from '@/util/formValidators';
import clsx from 'clsx';
import { Form } from 'informed';
import Image from 'next/image';
import { Fragment } from 'react';
import defaultClasses from './contactPage.module.css';
import { useContactPage } from './useContactPage';

const BANNER_IDENTIFIER = 'contact-us-banner';
const SIDEBAR_IDENTIFIER = 'contact-us-sidebar';

export const ContactPage = props => {
  const { classes: propClasses } = props;
  const classes = useStyle(defaultClasses, propClasses);
  const talonProps = useContactPage({
    cmsBlockIdentifiers: [BANNER_IDENTIFIER, SIDEBAR_IDENTIFIER],
  });

  const { errors, handleSubmit, isBusy, response, formApiRef } = talonProps;
  const { callUsSchedules } = getCurrentStoreContactConfig();

  const errorInfo =
    response &&
    (response.status ? (
      <span className={classes.succeeInfo}>Message Received! We will be in touch with you in 36 hours!</span>
    ) : (
      <span className={classes.errorInfo}>{response.content}</span>
    ));

  const maybeLoadingIndicator = isBusy ? fullPageLoadingIndicator : null;

  return (
    <Fragment>
      <div className={classes.contactus}>
        {maybeLoadingIndicator}
        <article
          className={classes.root}
          data-cy='ContactPage-root'
        >
          <Image
            src={banner1}
            alt=''
          />
          <h1 className={classes.title}>Contact Us</h1>

          <p className={classes.subtitle}>
            If you have any questions, please feel free to contact us. We're always here to assist you!
          </p>
          <div className={classes.conent}>
            <div className={classes.formcon}>
              <h1 className={classes.formtitle}>Ask:</h1>
              <div
                className={classes.formContainer}
                data-cy='ContactPage-formContainer'
              >
                <Form
                  className={classes.form}
                  onSubmit={handleSubmit}
                  formApiRef={formApiRef}
                  autocomplete='off'
                  suppressHydrationWarning
                >
                  <Field
                    id='contact-name'
                    label={<span className='text-[15px]'>Name</span>}
                    required
                    className={classes.formfield}
                  >
                    <TextInput
                      autoComplete='off'
                      field='name'
                      id='contact-name'
                      validate={isRequired}
                      suppressHydrationWarning
                    />
                  </Field>
                  <Field
                    id='contact-email'
                    label={<span className='text-[15px]'>Email</span>}
                    required
                    className={classes.formfield}
                  >
                    <TextInput
                      autoComplete='off'
                      field='email'
                      id='contact-email'
                      validate={isRequired}
                      suppressHydrationWarning
                    />
                  </Field>
                  <Field
                    id='contact-telephone'
                    label={<span className='text-[15px]'>Telephone</span>}
                  >
                    <TextInput
                      autoComplete='off'
                      field='telephone'
                      id='contact-telephone'
                      suppressHydrationWarning
                    />
                  </Field>
                  <Field
                    id='contact-comment'
                    label={<span className='text-[15px]'>Comment</span>}
                    required
                    className={classes.formfield}
                  >
                    <TextArea
                      autoComplete='off'
                      field='comment'
                      id='contact-comment'
                      validate={isRequired}
                      classes={{
                        input: classes.textareainput,
                      }}
                      suppressHydrationWarning
                    />
                  </Field>
                  <div className={classes.buttonsContainer}>
                    <Button
                      priority='high'
                      type='submit'
                      disabled={isBusy}
                      className={clsx(' h-12 w-full ', 'rounded-[3px] text-lg uppercase', 'aw-primary-btn')}
                      data-cy='submit'
                    >
                      Send
                    </Button>
                  </div>
                  <FormError
                    allowErrorMessages
                    errors={Array.from(errors.values())}
                  />
                  {errorInfo}
                </Form>
              </div>
            </div>
            <div className={classes.otherinfo}>
              <div className='mb-5 w-full bg-f8f3ef p-2.5 lg_mb-0 lg_px-9 lg_py-8'>
                <h1 className={classes.othertitle}>Our Email Addresses</h1>
                <p className='mb-5 flex justify-between'>
                  <span>Customer Service:</span>
                  <span>service@cs.awbridal.com</span>
                </p>
                <p className='mb-5 flex justify-between'>
                  <span>Cooperations:</span>
                  <span>Social@awbridal.com</span>
                </p>
                <p className='flex justify-between'>
                  <span>Wholesale:</span>
                  <span>wholesale@awbridal.com</span>
                </p>
              </div>
              <div className='mb-5 w-full bg-f8f3ef p-2.5 lg_mb-0 lg_px-9 lg_py-8'>
                <h1 className={classes.othertitle}>Call Us</h1>
                {callUsSchedules.map((schedule, scheduleIndex) => (
                  <Fragment key={`${schedule.telephoneDisplay}-${scheduleIndex}`}>
                    <p className={scheduleIndex > 0 ? 'mt-5' : ''}>Telephone: {schedule.telephoneDisplay}</p>
                    {schedule.serviceHours.map(serviceHour => (
                      <p key={`${schedule.telephoneDisplay}-${serviceHour}`}>{serviceHour}</p>
                    ))}
                  </Fragment>
                ))}
              </div>
              <div className='mb-5 w-full bg-f8f3ef p-2.5 lg_mb-0 lg_px-9 lg_py-8'>
                <h1 className={classes.othertitle}>Let`s Connect</h1>
                <p>Follow us on social media:</p>
                <p className='mt-5 flex justify-between'>
                  <span>Join us on Instagram:</span>
                  <span className={classes.link}>
                    <a
                      target='_blank'
                      href='https://www.instagram.com/aw.bridal/'
                      className='underline lg_hover_text-80583F'
                    >
                      @aw.bridal
                    </a>
                  </span>
                  <span className={classes.link}>
                    <a
                      target='_blank'
                      href='https://www.instagram.com/explore/tags/awbridaldress/'
                      className='underline lg_hover_text-80583F'
                    >
                      #awbridaldresses
                    </a>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </Fragment>
  );
};
